import { CustomButton, DataType, SelectDropDown } from '@ui-kit'
import { NAMESPACES } from '../../utils/constants'
import { getUniqueProductNames, handleMaxRTE } from '../../utils/maxRTE'
import { SelectProgramProductPropsType } from '../../types/maxRTE.types'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { store, useAppSelector } from '../../utils/agent.store'
import { v4 as uuid } from 'uuid'
import {
  addToaster,
  DataTypeStore,
  DropdownType,
  fetchProductListAPI,
  getProgramsList,
  hideLoader,
  shouldCallMaxRTE,
  showLoader,
  updateMaxRTEShouldAutoScroll,
} from '@app-store'
import { CALL_INTERACTIONS, MAX_RTE_STRINGS } from '../../utils/strings'
import { HCPProgramType } from '../../types/permissionAssignment.types'

export const SelectProgramProduct = ({
  setOpenProductModal,
  patientId,
}: SelectProgramProductPropsType) => {
  const [programOptions, setProgramOptions] = useState<
    DropdownType<DataTypeStore>[]
  >([])
  const [errorMessage, setErrorMessage] = useState('')
  const [selectedProgramId, setSelectedProgramId] = useState<string>()
  const [productOptions, setProductOptions] = useState<DropdownType<string>[]>(
    []
  )
  const [selectedProductId, setSelectedProductId] =
    useState<DropdownType<DataType, string>>()
  const selectedTenantId = useAppSelector(
    (state) => state?.authUser?.selectedClientTenant?.id
  )
  const dispatch = useDispatch()
  const showError = (message: string) =>
    store.dispatch(addToaster({ id: uuid(), message, type: 'error' }))

  const handleProductSubmit = async () => {
    if (!patientId && !selectedProductId?.label) {
      setErrorMessage(MAX_RTE_STRINGS.PATIENT_PRODUCT_REQUIRED)
      return
    }
    if (!patientId) {
      setErrorMessage(MAX_RTE_STRINGS.PATIENT_REQUIRED)
      return
    }
    if (!selectedProductId?.label) {
      setErrorMessage(MAX_RTE_STRINGS.PRODUCT_REQUIRED)
      return
    }
    const checkShouldCallMaxRTE = await shouldCallMaxRTE(
      patientId,
      Number(selectedProductId?.value)
    )

    if (!checkShouldCallMaxRTE.data.consentsVerified) {
      await new Promise(() => {
        showError(MAX_RTE_STRINGS.HIPAA_CONSENT_MISSING_PRODUCT)
      })
    }

    await handleMaxRTE(
      patientId,
      selectedProductId?.label?.replace(/®/g, '').trim() ?? ''
    )
    dispatch(updateMaxRTEShouldAutoScroll(true))
    setOpenProductModal(false)
  }
  const fetchProducts = async (id: number) => {
    try {
      dispatch(showLoader())
      const response = await fetchProductListAPI(id)
      const products: DropdownType<string>[] = []
      response?.data?.forEach(
        (element: { id: number; trademarked_name: string }) => {
          products.push({
            value: element.id?.toString(),
            label: element.trademarked_name,
          })
        }
      )
      setProductOptions(
        getUniqueProductNames(products)?.map((item) => ({
          ...item,
          label: item?.label,
        }))
      )
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchPrograms = async () => {
    try {
      dispatch(showLoader())
      const programs = await getProgramsList()
      if (programs?.data?.content) {
        const programOptionsList: DropdownType<DataTypeStore>[] =
          programs?.data?.content
            ?.filter((item: HCPProgramType) => item.enabled)
            ?.map((item: HCPProgramType) => {
              return {
                label: item?.operational_program_name ?? '',
                value: item?.id?.toString() ?? '',
              }
            })
        setProgramOptions([...programOptionsList])
        setSelectedProgramId('')
        setProductOptions([])
      }
    } catch (err) {
      console.log(err)
      setProgramOptions([])
    } finally {
      dispatch(hideLoader())
    }
  }
  const handleProgramChange = (e: DropdownType<unknown, string>) => {
    setSelectedProgramId(e?.value)
    setSelectedProductId(undefined)
    setErrorMessage('')
  }
  useEffect(() => {
    fetchPrograms()
  }, [selectedTenantId])

  useEffect(() => {
    if (selectedProgramId) fetchProducts(Number(selectedProgramId))
    else setProductOptions([])
  }, [selectedProgramId])
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex justify-between w-full">
        <div className="font-medium text-[14px] leading-[18px] align-middle accent-900">
          {MAX_RTE_STRINGS.PRODUCT_MODAL_HEADING}
        </div>
        <div className="flex gap-3">
          <CustomButton
            dataQaId="cancel"
            color="primary"
            tabIndex={0}
            type="button"
            onClick={() => setOpenProductModal(false)}
          >
            {NAMESPACES.CANCEL_BUTTON}
          </CustomButton>
          <CustomButton
            isFilled
            color="primary"
            type="button"
            dataQaId="save"
            tabIndex={0}
            onClick={handleProductSubmit}
          >
            {NAMESPACES.SUBMIT}
          </CustomButton>
        </div>
      </div>
      <div className="flex justify-between w-full gap-2 [&>*]:flex-1 [&>*]:min-w-0 ">
        <SelectDropDown
          label={CALL_INTERACTIONS.PROGRAM}
          options={programOptions}
          name=""
          onChange={handleProgramChange}
          dataQaId="program"
          className="bg-white"
          defaultValue={selectedProgramId ?? ''}
          required={true}
        />
        <SelectDropDown
          label={CALL_INTERACTIONS.PRODUCT}
          options={productOptions}
          name=""
          onChange={(val) => {
            setSelectedProductId(val)
            setErrorMessage('')
          }}
          dataQaId="product"
          className="bg-white"
          required={true}
        />
      </div>
      {errorMessage && (
        <div className="text-error-500 text-xs font-primary font-normal w-full text-wrap inline-block leading-[1.3]">
          {errorMessage}
        </div>
      )}
    </div>
  )
}
